import { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Layout } from '../components/Layout';
import { useQuotas } from '../hooks/useQuotas';
import type { APIActivity } from '../hooks/useQuotas';

const statusColor = (status: string) => {
  if (status === 'EXCEEDED') return 'error';
  if (status === 'WARNING') return 'warning';
  return 'success';
};

const progressColor = (status: string): 'error' | 'warning' | 'success' => {
  if (status === 'EXCEEDED') return 'error';
  if (status === 'WARNING') return 'warning';
  return 'success';
};

const API_COLORS: Record<string, string> = {
  '3d-align': '#1976d2',
  'boltz-3d-affinity': '#2e7d32',
  '3d-dock': '#ed6c02',
};

function ActivityChart({ activity }: { activity: APIActivity[] }) {
  const [range, setRange] = useState<7 | 30>(7);

  if (!activity || activity.length === 0) {
    return (
      <Alert severity="info">
        No activity data yet. Data will appear once requests are processed.
      </Alert>
    );
  }

  const allDates = activity[0]?.days.map((d) => d.date) || [];
  const dates = allDates.slice(-range);

  const chartData = dates.map((date) => {
    const point: Record<string, any> = { date: date.slice(5) };
    activity.forEach((a) => {
      const day = a.days.find((d) => d.date === date);
      point[a.api] = day?.count || 0;
    });
    return point;
  });

  const hasData = chartData.some((d) =>
    activity.some((a) => (d[a.api] as number) > 0)
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1">Daily requests</Typography>
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={(_, v) => v && setRange(v)}
            size="small"
          >
            <ToggleButton value={7}>7 days</ToggleButton>
            <ToggleButton value={30}>30 days</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {!hasData ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No requests recorded yet for this period.
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {activity.map((a) => (
                <Line
                  key={a.api}
                  type="monotone"
                  dataKey={a.api}
                  stroke={API_COLORS[a.api] || '#9e9e9e'}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export function ActivityContent() {
  const { activity, loading, error, refetch } = useQuotas();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={refetch}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        API usage over time.
      </Typography>
      <ActivityChart activity={activity} />
    </>
  );
}

type QuotaUser = {
  user_id: string;
  email?: string;
  quota_used: number;
  quota_max: number;
  percentage: number;
  status: string;
};

type QuotaUserSortKey = 'email' | 'quota_used' | 'quota_max' | 'percentage' | 'status';

function QuotaUsersTable({ users }: { users: QuotaUser[] }) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<QuotaUserSortKey>('quota_used');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const handleSort = (column: QuotaUserSortKey) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  const filteredSorted = (() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? users.filter((u) =>
          (u.email ?? '').toLowerCase().includes(q) ||
          u.user_id.toLowerCase().includes(q)
        )
      : users;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'email':
          return ((a.email ?? a.user_id).localeCompare(b.email ?? b.user_id)) * dir;
        case 'quota_used':
          return (a.quota_used - b.quota_used) * dir;
        case 'quota_max':
          return (a.quota_max - b.quota_max) * dir;
        case 'percentage':
          return (a.percentage - b.percentage) * dir;
        case 'status':
          return a.status.localeCompare(b.status) * dir;
        default:
          return 0;
      }
    });
  })();

  const paged = filteredSorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <TextField
          size="small"
          placeholder="Search email or ID..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 240 }}
        />
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'email' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortDir : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={sortBy === 'quota_used' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'quota_used'}
                  direction={sortBy === 'quota_used' ? sortDir : 'asc'}
                  onClick={() => handleSort('quota_used')}
                >
                  Used
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={sortBy === 'quota_max' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'quota_max'}
                  direction={sortBy === 'quota_max' ? sortDir : 'asc'}
                  onClick={() => handleSort('quota_max')}
                >
                  Max
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={sortBy === 'percentage' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'percentage'}
                  direction={sortBy === 'percentage' ? sortDir : 'asc'}
                  onClick={() => handleSort('percentage')}
                >
                  %
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sortDirection={sortBy === 'status' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortDir : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No users match your search.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paged.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell sx={{ fontSize: '0.85rem' }}>
                    {user.email || user.user_id}
                  </TableCell>
                  <TableCell align="right">{user.quota_used}</TableCell>
                  <TableCell align="right">{user.quota_max}</TableCell>
                  <TableCell align="right">{user.percentage}%</TableCell>
                  <TableCell align="center">
                    <Chip label={user.status} color={statusColor(user.status)} size="small" variant="outlined" />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredSorted.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[20, 50, 100]}
      />
    </>
  );
}

export function QuotasContent() {
  const { quotas, loading, error, refetch } = useQuotas();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={refetch}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Monthly quota usage per API.
      </Typography>
      <Grid container spacing={3}>
        {quotas.map((quota) => (
          <Grid item xs={12} key={quota.api}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="subtitle1" fontFamily="monospace" sx={{ textTransform: 'uppercase' }}>
                    {quota.api}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Global usage
                    </Typography>
                    <Typography variant="body2">
                      {quota.quota_used} / {quota.quota_max} ({quota.usage_percent}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(quota.usage_percent, 100)}
                    color={progressColor(quota.status)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Box display="flex" gap={2} mb={2}>
                  <Typography variant="body2">
                    <strong>{quota.total_users}</strong> users
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    <strong>{quota.users_ok}</strong> OK
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    <strong>{quota.users_warning}</strong> Warning
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    <strong>{quota.users_exceeded}</strong> Exceeded
                  </Typography>
                </Box>

                {quota.users && quota.users.length > 0 && (
                  <QuotaUsersTable users={quota.users} />
                )}

                {(!quota.users || quota.users.length === 0) && (
                  <Alert severity="info" sx={{ mt: 1 }}>No user data available</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
}

export const QuotasPage = () => (
  <Layout>
    <Typography variant="h5" gutterBottom>Quotas</Typography>
    <QuotasContent />
  </Layout>
);
